import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import moment from 'moment';

import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';
import { UsersService } from 'src/services/users.service';

import { STRIPE_PRODUCTS } from '../constants';
import { sleep } from '../utils/utils';

@Component({
  selector: 'app-upgrade',
  templateUrl: './upgrade.page.html',
  styleUrls: ['./upgrade.page.scss'],
})
export class UpgradePage implements OnInit {
  title = 'Loading...';
  isLoading: boolean;
  numberOfSeats: number;
  seatMinimum = 5;

  // Current subscription
  isSubscribed: boolean;
  subscriptionStripeId: string;
  subscriptionPriceId: string;
  subscriptionProductId: string;
  isMissingPaymentMethod: boolean;
  isLegacyPlan: boolean;

  // Custom price
  isCustomPrice: boolean;
  upgradePriceId: string;
  upgradeProductId: string;

  // Available plans
  plans: any[];

  // Loading
  trialExpirationBadge: string;
  shouldShowSuccessBanner: boolean;
  isLoadingCheckout: boolean;
  isFailed: boolean;

  // Enterprise plan config
  enterpriseFeatures = [
    'Custom integrations',
    'Mass email broadcasts',
    'Single sign-on (SSO)',
    'HRIS integration & Directory Sync',
    'Dedicated hosting environment',
    'Industry-leading SLAs',
  ];

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private msgService: MessageService,
    private usersSrvc: UsersService,
    private route: ActivatedRoute,
    private tenantsService: TenantsService,
    private analyticsService: AnalyticsService,
    private navCtrl: NavController,
  ) {}

  ngOnInit(): void {
    this.getInitialData();

    this.route.queryParams.subscribe(async (params) => {
      if (params.success) {
        this.shouldShowSuccessBanner = true;
        await sleep(3000);

        const _refreshedTenant = await this.tenantsService.getTenant();
        this.navCtrl.navigateRoot('/dashboard/home');
      }
    });
  }

  async getInitialData() {
    this.isFailed = false;

    const tenant = this.authService.tenant;
    this.isSubscribed = this.authService.tenant.accountStatus === 'active';
    this.seatMinimum = tenant.billing?.seatsMinimum || 5;
    this.numberOfSeats = this.seatMinimum;

    if (tenant.trialEndDate && !this.isSubscribed) {
      const end = moment(tenant.trialEndDate);

      if (end.diff(moment()) < 0) {
        this.trialExpirationBadge = `Your free trial ended ${moment(tenant.trialEndDate).format('MMMM D')}`;
      }
    }

    await Promise.all([this.getSubscription(), this.getAvailablePlans()]);

    this.upgradePriceId = tenant.billing?.upgradePriceId;
    this.upgradeProductId = tenant.billing?.upgradeProductId;

    if (
      this.isLegacyPlan ||
      tenant.billing?.checkoutDisabled ||
      (this.isSubscribed && !this.subscriptionStripeId) ||
      (!this.isSubscribed && this.upgradePriceId && this.upgradeProductId)
    ) {
      this.isCustomPrice = true;
    }

    this.title = this.isSubscribed ? 'Manage Your Subscription' : 'Upgrade Your Account';
  }

  async getSubscription() {
    this.isLoading = true;
    try {
      const result: any = await this.apiService.getPromise('/stripe/subscription');
      const subscription = result.subscription;
      this.subscriptionStripeId = subscription.id;
      this.isMissingPaymentMethod = !result.paymentMethod?.data?.length;

      // Check for legacy plans
      if (this.isSubscribed) {
        const legacyProductIds = [
          STRIPE_PRODUCTS.PER_CHANNEL_BASIC.stripeProductId,
          STRIPE_PRODUCTS.PER_CHANNEL_PRO.stripeProductId,
          STRIPE_PRODUCTS.FREE.stripeProductId,
        ];

        this.isLegacyPlan = subscription.items.data.some((item: any) => legacyProductIds.includes(item.plan.product));
      }

      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  }

  async getAvailablePlans() {
    this.isLoading = true;
    try {
      const result: any = await this.apiService.getPromise('/stripe/plans');
      this.plans = result.data;
    } catch (error) {
      this.msgService.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async openCheckout(plan?: any) {
    this.isLoadingCheckout = true;
    try {
      const payload = plan
        ? {
            priceId: plan.stripePriceId,
            productId: plan.stripeProductId,
            restrictedFeatures: plan.restrictedFeatures,
            seats: this.numberOfSeats,
          }
        : {
            priceId: this.upgradePriceId,
            productId: this.upgradeProductId,
            seats: this.numberOfSeats,
          };
      const session: any = await this.apiService.postPromise('/stripe/generate-checkout-session', payload);
      const checkoutUrl = session.url;
      this.isLoadingCheckout = false;
      this.isFailed = false;

      window.open(checkoutUrl, '_self');
    } catch (error) {
      this.msgService.showError(error);
      this.isFailed = true;
      this.isLoadingCheckout = false;
    }
  }

  async manageSubscription() {
    this.isLoadingCheckout = true;
    try {
      const session: any = await this.apiService.postPromise('/stripe/generate-billing-session', {});
      window.open(session.url, '_blank');
      this.isLoadingCheckout = false;
    } catch (error) {
      this.isLoadingCheckout = false;
    }
  }

  async addPaymentMethod() {
    this.analyticsService.trackEvent('Upgrade', 'Add Payment Method', {
      isCustomPrice: this.isCustomPrice,
    });

    this.isLoadingCheckout = true;

    try {
      const session: any = await this.apiService.postPromise('/stripe/generate-billing-session', {
        flowData: {
          type: 'payment_method_update',
          after_completion: {
            type: 'redirect',
            redirect: {
              return_url: `${window.location.href}?success=1`,
            },
          },
        },
      });
      window.open(session.url, '_self');
    } catch (error) {
      this.msgService.showError(error);
      this.isFailed = true;
      this.isLoadingCheckout = false;
    }
  }

  async updateSubscription(plan: any) {
    this.isLoadingCheckout = true;
    try {
      await this.apiService.putPromise('/stripe/subscription', {
        productId: plan.stripeProductId,
        priceId: plan.stripePriceId,
        restrictedFeatures: plan.restrictedFeatures,
      });

      this.isLoadingCheckout = false;

      this.msgService.show('Successully updated your subscription!');

      this.getInitialData();
    } catch (error) {
      this.msgService.showError(error);
      this.isLoadingCheckout = false;
    }
  }
}
